import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  ArrowLeftIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import { resourcesAPI, clientsAPI, facilitiesAPI, placementsAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface EditFormData {
  relatedToType: string;
  relatedToId: string;
  category: string;
  description?: string;
  tags: string;
  isConfidential: boolean;
  accessLevel: string;
  expirationDate?: string;
}

const ResourceEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<EditFormData>();

  const relatedToType = watch('relatedToType');

  // Fetch resource details
  const {
    data: resourceData,
    isLoading: isLoadingResource
  } = useQuery({
    queryKey: ['resource', id],
    queryFn: () => resourcesAPI.getResource(id!),
    enabled: !!id,
  });

  const resource = resourceData?.data?.data?.resource;

  // Fetch related objects for dropdown
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsAPI.getClients(),
    enabled: relatedToType === 'Client',
  });

  const {
    data: facilitiesData,
    isLoading: isLoadingFacilities
  } = useQuery({
    queryKey: ['facilities'],
    queryFn: () => facilitiesAPI.getFacilities(),
    enabled: relatedToType === 'Facility',
  });

  const {
    data: placementsData,
    isLoading: isLoadingPlacements
  } = useQuery({
    queryKey: ['placements'],
    queryFn: () => placementsAPI.getPlacements(),
    enabled: relatedToType === 'Placement',
  });

  const clients = clientsData?.data?.data?.clients || [];
  const facilities = facilitiesData?.data?.data?.facilities || [];
  const placements = placementsData?.data?.data?.placements || [];

  // Populate form when resource data loads
  useEffect(() => {
    if (resource) {
      setValue('relatedToType', resource.relatedTo.objectType);
      setValue('relatedToId', resource.relatedTo.objectId);
      setValue('category', resource.category);
      setValue('description', resource.description || '');
      setValue('tags', resource.tags?.join(', ') || '');
      setValue('isConfidential', resource.isConfidential);
      setValue('accessLevel', resource.accessLevel);
      
      if (resource.expirationDate) {
        setValue('expirationDate', new Date(resource.expirationDate).toISOString().split('T')[0]);
      }
    }
  }, [resource, setValue]);

  // Update resource mutation
  const updateMutation = useMutation({
    mutationFn: (data: EditFormData) => {
      const updateData = {
        relatedTo: {
          objectType: data.relatedToType,
          objectId: data.relatedToId
        },
        category: data.category,
        description: data.description || undefined,
        tags: data.tags ? data.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        isConfidential: data.isConfidential,
        accessLevel: data.accessLevel,
        expirationDate: data.expirationDate || undefined
      };
      
      return resourcesAPI.updateResource(id!, updateData);
    },
    onSuccess: () => {
      showSuccess('Resource updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['resource', id] });
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      navigate(`/resources/${id}`);
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to update resource');
    },
  });

  const onSubmit = (data: EditFormData) => {
    updateMutation.mutate(data);
  };

  const getFileIcon = (resource: any) => {
    if (resource?.isImage) {
      return <PhotoIcon className="w-8 h-8 text-green-600" />;
    } else if (resource?.isVideo) {
      return <VideoCameraIcon className="w-8 h-8 text-blue-600" />;
    } else {
      return <DocumentIcon className="w-8 h-8 text-gray-600" />;
    }
  };

  if (isLoadingResource || isLoadingClients || isLoadingFacilities || isLoadingPlacements) {
    return <LoadingSpinner />;
  }

  if (!resource) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Resource not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Link
            to={`/resources/${id}`}
            className="inline-flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Resource
          </Link>
        </div>
        <div className="mt-4">
          <h1 className="text-2xl font-bold text-gray-900 text-left">Edit Resource</h1>
          <p className="text-gray-600 mt-2">
            Update resource information and metadata
          </p>
        </div>
      </div>

      {/* Current File Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current File</h3>
        <div className="flex items-center space-x-4">
          {getFileIcon(resource)}
          <div>
            <div className="text-lg font-medium text-gray-900">
              {resource.originalName}
            </div>
            <div className="text-sm text-gray-600">
              {resource.fileType?.toUpperCase()} • {resource.fileSizeFormatted} • Version {resource.version}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Resource Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Related To Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Related To *
              </label>
              <select
                {...register('relatedToType', { required: 'Related to type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Client">Client</option>
                <option value="Facility">Facility</option>
                <option value="Placement">Placement</option>
                <option value="Task">Task</option>
              </select>
              {errors.relatedToType && (
                <p className="mt-1 text-sm text-red-600">{errors.relatedToType.message}</p>
              )}
            </div>

            {/* Related To Object */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select {relatedToType} *
              </label>
              <select
                {...register('relatedToId', { required: 'Related object is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select {relatedToType?.toLowerCase()}...</option>
                {relatedToType === 'Client' && clients.map((client: any) => (
                  <option key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </option>
                ))}
                {relatedToType === 'Facility' && facilities.map((facility: any) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.name} - {facility.address?.city}, {facility.address?.state}
                  </option>
                ))}
                {relatedToType === 'Placement' && placements.map((placement: any) => (
                  <option key={placement._id} value={placement._id}>
                    {placement.client?.firstName} {placement.client?.lastName} → {placement.facility?.name}
                  </option>
                ))}
              </select>
              {errors.relatedToId && (
                <p className="mt-1 text-sm text-red-600">{errors.relatedToId.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                {...register('category', { required: 'Category is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Medical Records">Medical Records</option>
                <option value="Insurance Documents">Insurance Documents</option>
                <option value="Legal Documents">Legal Documents</option>
                <option value="Identification">Identification</option>
                <option value="Photos">Photos</option>
                <option value="Assessments">Assessments</option>
                <option value="Care Plans">Care Plans</option>
                <option value="Contracts">Contracts</option>
                <option value="Facility Brochures">Facility Brochures</option>
                <option value="Licenses">Licenses</option>
                <option value="Inspection Reports">Inspection Reports</option>
                <option value="Other">Other</option>
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            {/* Access Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Level *
              </label>
              <select
                {...register('accessLevel', { required: 'Access level is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="restricted">Restricted</option>
              </select>
              {errors.accessLevel && (
                <p className="mt-1 text-sm text-red-600">{errors.accessLevel.message}</p>
              )}
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date
              </label>
              <input
                type="date"
                {...register('expirationDate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Confidential */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('isConfidential')}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Mark as Confidential
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe the document contents..."
            />
          </div>

          {/* Tags */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              {...register('tags')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter tags separated by commas..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Add tags to help organize and find documents later (e.g., urgent, signed, reviewed)
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to={`/resources/${id}`}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Updating...' : 'Update Resource'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceEdit;
